import React, { useState, useEffect, Suspense } from "react";
import axios from "axios";
import "./Blogs.css";
import { Link } from "react-router-dom";
import { FaCalendarDay } from "react-icons/fa";
import Seo from "../seo/Seo";
import Breadcrumbs from "./Breadcrumbs";
import { blogPath } from "../seo/blogPaths";
import blogBoot from "../seo/blogBoot.json";

const bootLatest = Array.isArray(blogBoot.latest) ? blogBoot.latest : [];
const bootCategories = Array.isArray(blogBoot.categories) ? blogBoot.categories : [];

const Blogs = () => {
  const [latestBlogs, setLatestBlogs] = useState(bootLatest);
  const [blogs, setBlogs] = useState(bootCategories);
  const [isLoading, setIsLoading] = useState(
    bootLatest.length === 0 && bootCategories.length === 0
  );
  const getDate = (dateString) => {
    const options = { year: "numeric", month: "short", day: "numeric" };
    return new Date(dateString).toLocaleDateString("en-US", options);
  };

  useEffect(() => {
    fetchBlogs();
  }, []);

  useEffect(() => {
    const animateBlogs = () => {
      const blogItems = document.querySelectorAll(".blog-item");

      blogItems.forEach((item, index) => {
        setTimeout(() => {
          item.classList.add("visible");
        }, index * 100); // Adjust the delay as needed
      });
    };
    // Call animateBlogs after the blogs have been set
    animateBlogs();
  }, [blogs]);

  const AWS_S3_BUCKET_URL = process.env.REACT_APP_AWS_S3_BUCKET_URL;
  const fetchBlogs = async () => {
    const categories = [];
    const blogsArray = [];
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_URL}/v1/api/public-api/category-with-blogs`
      );
      const getLatesBlogs = response.data.getLatestBlogs;
      const categoryWithBlogs = response.data.categoryWithBlogs;
      categoryWithBlogs.forEach((blog) => {
        if (blog.Blogs?.length > 0) categories.push(blog.id);
      });
      setLatestBlogs(getLatesBlogs || []);

      for (const category of categories) {
        const categoryResponse = await axios.get(
          `${process.env.REACT_APP_BASE_URL}/v1/api/public-api/category-with-blogs-by-id/${category}`
        );
        blogsArray.push(categoryResponse.data.categoryWithBlogs);
      }
      setBlogs(blogsArray);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  function capitalizeFirstLetter(string) {
    if (!string) {
      return ""; // Handle cases where the string is empty or undefined
    }
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const featured = latestBlogs[0];
  const featuredPath = featured ? blogPath(featured.slug, featured.title) : "";

  return (
    <div className="container" style={{ marginTop: "100px", minHeight: isLoading ? "70vh" : undefined }}>
      <Seo
        title="Networking & Digital Business Card Blog | Connct"
        description="Tips on NFC business cards, networking and events from Connct, for individuals and teams in India and the Middle East."
        path="/blogs"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blogs" },
        ]}
      />
      <Breadcrumbs
        items={[
          { name: "Home", path: "/" },
          { name: "Blog", path: "/blogs" },
        ]}
      />
      <h1 className="h2" style={{ fontWeight: 800, marginBottom: "24px" }}>
        Networking and digital business card insights
      </h1>
      <div className="latest-blogs">
        {featured && featuredPath ? (
        <Link
          className="latest-blogs-first blog-item"
          to={featuredPath}
          style={{ borderRadius: "10px" }}
        >
          <div className="zoom-effect">
            <img
              alt={featured.title || "Connct blog article"}
              className="img-fluid"
              src={`https://${AWS_S3_BUCKET_URL}.s3.ap-south-1.amazonaws.com/${featured.image}`}
              width={640}
              height={360}
              style={{ borderRadius: "10px" }}
            />
          </div>
          <h5>{featured.title}</h5>
          <div className="date">
            <FaCalendarDay style={{ marginLeft: 0 }} />
            <div style={{ lineHeight: "2em" }}>
              &nbsp;{getDate(featured.createdAt)}
            </div>
          </div>
          <div className="description">{featured.metaDesc}</div>
          <div className="category-tag">
            <span style={{ color: "red" }}>
              {capitalizeFirstLetter(featured.Category?.name)}
            </span>{" "}
            <span style={{ color: "green" }}>
              {capitalizeFirstLetter(featured?.Subcategory?.name)}
            </span>
          </div>
        </Link>
        ) : null}

        <div>
          {latestBlogs?.map((latestBlog, index) => {
              const path = blogPath(latestBlog.slug, latestBlog.title);
              if (index === 0 || !path) return null;
              return (
                <Link
                  key={index}
                  className="latest-blog-item blog-item"
                  to={path}
                  style={{ borderRadius: "10px" }}
                >
                  <div className="zoom-effect">
                    <img
                      alt={latestBlog.title || "Connct blog article"}
                      src={`https://${AWS_S3_BUCKET_URL}.s3.ap-south-1.amazonaws.com/${latestBlog.image}`}
                      width={640}
                      height={360}
                    />
                  </div>
                  <div className="latest-blog-item-data">
                    <h5>{latestBlog.title}</h5>
                    <div className="date">
                      <FaCalendarDay style={{ marginLeft: 0 }} />
                      <div style={{ lineHeight: "2em" }}>
                        {getDate(latestBlog.createdAt)}{" "}
                        {/* Use createdAt instead of updatedAt */}
                      </div>
                    </div>
                    <div className="description">{latestBlog.metaDesc}</div>
                    <div className="category-tag">
                      <span style={{ color: "red" }}>
                        {latestBlog.Category?.name}
                      </span>{" "}
                      <span style={{ color: "green" }}>
                        {latestBlog?.Subcategory?.name}
                      </span>
                    </div>
                  </div>
                </Link>
              );
          })}
        </div>
      </div>

      <div className="blog-grid">
        {blogs.map((category) =>
          category.Blogs.map((blog, blogIndex) => {
            const path = blogPath(blog.slug, blog.title);
            if (!path) return null;
            return (
            <Suspense key={blogIndex} fallback={<p>Loading...</p>}>
              <div
                className="blog-item"
                key={blogIndex}
                style={{ borderRadius: "7px" }}
              >
                <Link to={path}>
                  <img
                    alt={blog.title || "Connct blog article"}
                    className={`img-fluid zoom-effect`}
                    src={`https://${AWS_S3_BUCKET_URL}.s3.ap-south-1.amazonaws.com/${blog.image}`}
                    width={640}
                    height={360}
                    style={{ borderRadius: "10px" }}
                  />
                  <h5>{blog.title}</h5>
                  <div className="date">
                    <FaCalendarDay style={{ marginLeft: 0 }} />
                    <div style={{ lineHeight: "2em" }}>
                      {getDate(blog.updatedAt)}
                    </div>
                  </div>
                  <div className="description">{blog.metaDesc}</div>
                  <div className="category-tag">
                    <span style={{ color: "red" }}>
                      {category.name.charAt(0).toUpperCase() +
                        category.name.slice(1)}
                    </span>{" "}
                    <span style={{ color: "green" }}>
                      {blog?.Subcategory?.name
                        ? blog.Subcategory.name.charAt(0).toUpperCase() +
                          blog.Subcategory.name.slice(1)
                        : ""}
                    </span>
                  </div>
                </Link>
              </div>
            </Suspense>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Blogs;
